/**
 * glør Warenkorb — setzt Gratis-Versand-Balken und Vertrauensleiste in den
 * Horizon-Warenkorb (Cart-Drawer UND Warenkorbseite), ohne eine Core-Datei
 * anzufassen.
 *
 * Das Markup rechnet der Server: sections/glr-cart-extras.liquid liegt in der
 * Footer-Gruppe (also auf jeder Seite) und liefert es in zwei <template>.
 * Dieses Element
 *   1. setzt die Inhalte ein: Balken als erstes Kind von .cart-items__wrapper
 *      (ueber den Produkten), Duft-Vorschlaege direkt nach dem Warenkorb-
 *      Formular (unter den Produkten), Leiste direkt nach .cart__ctas (unter
 *      „Zur Kasse"),
 *   2. setzt sie nach jedem Morph von Horizon wieder ein — der
 *      MutationObserver laeuft vor dem naechsten Bild, es flackert nichts,
 *   3. holt nach jeder Warenkorb-Aenderung frisches Markup ueber die Section
 *      Rendering API (`?sections=<section.id>`) und aktualisiert den Balken
 *      an Ort und Stelle, damit die Fuellung animiert.
 *
 * Aenderungen erkennt es doppelt: an den Warenkorb-Ereignissen (gleiche Namen
 * wie assets/glr-cart-bundles.js) UND am Fingerabdruck aus Summe und
 * Stueckzahl, den Horizon nach jedem Morph neu ins DOM schreibt. Damit sind
 * auch Rabattcodes und kuenftig umbenannte Ereignisse abgedeckt.
 */
const CHANGE_EVENTS = [
  // Horizon 4.x: StandardEvents.cartLinesUpdate, traegt `promise`.
  'shopify:cart:lines-update',
  'shopify:cart:update',
  'cart:lines-update',
  'cart:update',
  'CartUpdateEvent',
  'cart:refresh',
];

const SLOTS = {
  bar: {
    targets: () =>
      [...document.querySelectorAll('.cart-items__wrapper')].filter((el) => el.querySelector('form.cart-form')),
    find: (target) => target.querySelector(':scope > [data-glr-cart-extra="bar"]'),
    place: (target, node) => target.prepend(node),
  },
  upsell: {
    targets: () =>
      [...document.querySelectorAll('.cart-items__wrapper')].filter((el) => el.querySelector(':scope > form.cart-form')),
    find: (target) => target.querySelector(':scope > [data-glr-cart-extra="upsell"]'),
    // Hinter den serverseitigen Garantie-Vorschlag (snippets/glr-cart-guarantee), falls da.
    place: (target, node) =>
      (target.querySelector(':scope > .glr-cart-guar') || target.querySelector(':scope > form.cart-form')).after(node),
  },
  empty: {
    // Leerer Warenkorb: der Wrapper traegt dann kein Warenkorb-Formular.
    targets: () =>
      [...document.querySelectorAll('.cart-items__wrapper')].filter((el) => !el.querySelector('form.cart-form')),
    find: (target) => target.querySelector(':scope > [data-glr-cart-extra="empty"]'),
    place: (target, node) => target.append(node),
    // Haengt nicht am Warenkorb (siehe snippets/glr-cart-empty.liquid): das
    // Markup steht schon beim Seitenaufbau bereit und wird nie nachgeladen.
    // Deshalb sitzt es sofort, wenn die letzte Zeile verschwindet.
    static: true,
  },
  title: {
    // Horizon laesst den Titel im leeren Drawer weg — hier kommt er zurueck,
    // vor das Kreuz. Nur im Drawer; die Warenkorbseite hat eine eigene H1.
    targets: () => document.querySelectorAll('#cart-drawer .theme-drawer__header:not(:has(.theme-drawer__title))'),
    find: (target) => target.querySelector(':scope > [data-glr-cart-extra="title"]'),
    place: (target, node) => target.prepend(node),
    // Fester Text, haengt nicht am Warenkorb — wie der leere Korb.
    static: true,
  },
  trust: {
    targets: () => document.querySelectorAll('.cart__ctas'),
    find: (target) => {
      const next = target.nextElementSibling;
      return next?.matches('[data-glr-cart-extra="trust"]') ? next : null;
    },
    place: (target, node) => target.after(node),
  },
};

class GlrCartExtras extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.requestId = 0;
    this.fingerprint = this.#fingerprint();
    this.#readTemplates(this);

    // Im Theme-Editor wird die Section bei jeder Aenderung neu gerendert:
    // vorhandene Einsaetze sofort gegen die neue Fassung tauschen.
    this.#removeAll();
    this.#apply();

    this.onCartUpdate = (event) => this.#refresh(event);
    for (const name of CHANGE_EVENTS) document.addEventListener(name, this.onCartUpdate);

    this.onClick = (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-glr-upsell-add]') : null;
      if (button) this.#addUpsell(button);
    };
    document.addEventListener('click', this.onClick);

    this.observer = new MutationObserver(() => {
      this.#apply();
      const print = this.#fingerprint();
      if (print !== this.fingerprint) {
        this.fingerprint = print;
        this.#refresh();
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    for (const name of CHANGE_EVENTS) document.removeEventListener(name, this.onCartUpdate);
    document.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  /** Summe + Stueckzahl, wie Horizon sie nach jedem Morph ins DOM schreibt. */
  #fingerprint() {
    const total = document.querySelector('[data-cart-subtotal]')?.getAttribute('value') ?? '';
    const count = document.querySelector('[ref="cartItemCount"]')?.textContent.trim() ?? '';
    // Leer oder voll gehoert dazu: beim Leeren verschwinden Summe und Zahl,
    // und die Einsaetze wechseln komplett (Vorschlaege statt Balken/Leiste).
    const state = document.querySelector('.cart-items__wrapper:not(:has(form.cart-form))') ? 'e' : 'f';
    return `${total}|${count}|${state}`;
  }

  /**
   * Zahl der Warenkorbzeilen, wie sie im DOM steht. Der leere Warenkorb hat
   * keine Zaehler mehr, aber einen Wrapper ohne Formular — das zaehlt als 0.
   */
  #domCount() {
    if (document.querySelector('.cart-items__wrapper:not(:has(form.cart-form))')) return '0';
    const node = document.querySelector('[ref="cartItemCount"]');
    return node ? node.textContent.trim() : null;
  }

  /** Liest das Markup aus den <template> eines Wurzelelements. */
  #readTemplates(root) {
    const previous = this.html || {};
    this.html = {};
    for (const [key, slot] of Object.entries(SLOTS)) {
      // Statische Slots behalten die Fassung vom Seitenaufbau: eine Antwort,
      // die noch den Stand von davor traegt, darf sie nicht leer raeumen.
      if (slot.static && previous[key]) {
        this.html[key] = previous[key];
        continue;
      }
      const tpl = root.querySelector(`template[data-glr-slot="${key}"]`);
      this.html[key] = tpl ? tpl.innerHTML.trim() : '';
    }
    // Stand, auf den sich dieses Markup bezieht (data-cart-count der Section).
    const host = root instanceof Document ? root.querySelector('glr-cart-extras') : root;
    this.serverCount = host ? host.getAttribute('data-cart-count') : null;
  }

  #build(key) {
    if (!this.html[key]) return null;
    const wrap = document.createElement('div');
    wrap.innerHTML = this.html[key];
    const node = wrap.firstElementChild;
    if (!node) return null;
    node.setAttribute('data-glr-cart-extra', key);
    return node;
  }

  #removeAll() {
    for (const node of document.querySelectorAll('[data-glr-cart-extra]')) node.remove();
  }

  /**
   * Fehlende Einsaetze ergaenzen. Laeuft im Rueckruf des Beobachters, also
   * vor dem naechsten Bild. Idempotent: die eigenen Einfuegungen loesen den
   * Beobachter zwar erneut aus, finden dann aber alles an seinem Platz.
   */
  #apply() {
    for (const [key, slot] of Object.entries(SLOTS)) {
      for (const target of slot.targets()) {
        if (slot.find(target)) continue;
        const node = this.#build(key);
        if (node) slot.place(target, node);
      }
    }
  }

  /**
   * Nach einer Warenkorb-Aenderung frisches Markup holen.
   * @param {Event} [event]
   * @param {number} [attempt] - Zaehler fuer den Nachschlag bei altem Stand
   */
  async #refresh(event, attempt = 0) {
    const id = ++this.requestId;
    for (const node of document.querySelectorAll('[data-glr-cart-extra]')) {
      if (SLOTS[node.getAttribute('data-glr-cart-extra')]?.static) continue;
      node.classList.add('is-updating');
    }
    try {
      // Das Ereignis feuert, bevor die Aenderung durch ist; traegt es ein
      // Promise, darauf warten — sonst liefert der Server den alten Stand.
      if (event?.promise?.then) await event.promise.catch(() => {});
      const root = window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${root}?sections=${encodeURIComponent(this.sectionId)}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      if (id !== this.requestId) return; // eine neuere Anfrage laeuft
      const doc = new DOMParser().parseFromString(data[this.sectionId] || '', 'text/html');
      this.#readTemplates(doc);
      this.#update();
      this.fingerprint = this.#fingerprint();

      /*
       * Der Beobachter stoesst den Abruf schon waehrend des Morphs an; dann
       * kann der Server noch den Stand von davor liefern. Das faellt beim
       * Wechsel voll/leer auf: die Vorschlaege fehlen, obwohl der Warenkorb
       * leer ist. Widersprechen sich Antwort und DOM, kurz nachfassen.
       */
      const dom = this.#domCount();
      if (this.serverCount !== null && dom !== null && this.serverCount !== dom && attempt < 2) {
        setTimeout(() => this.#refresh(undefined, attempt + 1), 300);
      }
    } catch (error) {
      // Kein Netz o. ae.: alter Stand bleibt stehen, nur die Markierung weg.
    } finally {
      if (id === this.requestId) {
        for (const node of document.querySelectorAll('[data-glr-cart-extra]')) node.classList.remove('is-updating');
      }
    }
  }

  /** Einsaetze gegen die frische Fassung tauschen; den Balken nur anpassen. */
  #update() {
    for (const [key, slot] of Object.entries(SLOTS)) {
      // Unveraenderliche Einsaetze nicht antasten — ein Austausch waehrend des
      // Uebergangs waere genau das Flackern, das wir vermeiden wollen.
      if (slot.static) continue;
      for (const target of slot.targets()) {
        const current = slot.find(target);
        const fresh = this.#build(key);
        if (!fresh) {
          current?.remove();
          continue;
        }
        if (!current) {
          slot.place(target, fresh);
        } else if (key === 'bar' && current.classList.contains('glr-shipbar')) {
          this.#morphBar(current, fresh);
        } else {
          current.replaceWith(fresh);
        }
      }
    }
  }

  /**
   * Duft-Vorschlag in den Warenkorb legen. Muster aus assets/glr-cart-set.js:
   * Add per AJAX, CartLinesUpdateEvent mit Promise, das mit den frisch
   * gerenderten Warenkorb-Sections aufloest — Horizon morpht dann selbst.
   * @param {Element} button
   */
  async #addUpsell(button) {
    if (button.classList.contains('is-loading')) return;
    const id = button.getAttribute('data-glr-upsell-add');
    const box = button.closest('.glr-cart-upsell');
    const error = box?.querySelector('[data-glr-upsell-error]');
    if (error) error.hidden = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const body = { items: [{ id: Number(id), quantity: 1 }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    let CartLinesUpdateEvent = null;
    let deferred = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
      deferred = CartLinesUpdateEvent.createPromise();
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: id, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (e) {
      // Ohne Ereignis-Modul: Add laeuft trotzdem, danach selbst neu rendern.
    }

    const routes = window.Theme?.routes || {};
    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const res = await fetch(routes.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Add failed');

      if (deferred && CartLinesUpdateEvent) {
        const cart = await fetch(`${root}cart.js`).then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'glr-cart-extras', sections: data.sections, didError: false },
        });
      } else {
        window.location.reload();
      }
    } catch (err) {
      deferred?.reject?.(err);
      if (error) {
        error.textContent = document.documentElement.lang.startsWith('de')
          ? 'Konnte nicht in den Warenkorb gelegt werden.'
          : 'Could not be added to the cart.';
        error.hidden = false;
      }
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  /** Balken an Ort und Stelle aktualisieren, damit die Breite animiert. */
  #morphBar(current, fresh) {
    const pct = fresh.dataset.glrPct || '0';
    const track = current.querySelector('.glr-shipbar__track');
    const freshTrack = fresh.querySelector('.glr-shipbar__track');
    if (!track || !freshTrack) {
      current.replaceWith(fresh);
      return;
    }
    current.className = fresh.className;
    current.dataset.glrPct = pct;
    for (const child of [...current.children]) if (child !== track) child.remove();
    for (const child of [...fresh.children]) {
      if (child === freshTrack) continue;
      if (child.compareDocumentPosition(freshTrack) & Node.DOCUMENT_POSITION_FOLLOWING) {
        track.before(child);
      } else {
        current.append(child);
      }
    }
    for (const attr of ['aria-valuenow', 'aria-label']) {
      track.setAttribute(attr, freshTrack.getAttribute(attr) || '');
    }
    requestAnimationFrame(() => current.style.setProperty('--glr-shipbar-pct', `${pct}%`));
  }
}

if (!customElements.get('glr-cart-extras')) customElements.define('glr-cart-extras', GlrCartExtras);
